/**
 * Queue simulation state.
 *
 * The interesting piece is `commands`: everything the operator does is recorded
 * here rather than applied to a local model, and the server replays the whole
 * list from the seed on each run. That is what keeps one copy of the queueing
 * rules — the ones the factory runs — instead of a second copy in the browser
 * that would slowly disagree with it.
 */

import { createSlice, PayloadAction } from '@ufc/core'
import type {
  SimAvailableOrder,
  SimCommand,
  SimReplayableDay,
  SimRun,
  SimSeed,
  SimStartMode,
  SimVariantKey,
} from '../domain/types'

export interface SimulationState {
  /** Where the starting yard came from. */
  mode: SimStartMode
  /** The day being replayed, when the mode is `replay`. */
  date: string | null
  days: SimReplayableDay[]
  daysLoading: boolean

  /** Advancing on its own, for showing a day to a room. */
  playing: boolean

  seed: SimSeed | null
  seedLoading: boolean
  seedError: string | null

  /** Everything done since the seed was taken, in order. */
  commands: SimCommand[]
  /** How far the yard has been run forward, in minutes past `seed.liveAt`. */
  currentMin: number

  runs: SimRun[]
  running: boolean
  runError: string | null

  /** Which sets of rules to run side by side. Empty means the yard's own. */
  compare: SimVariantKey[]

  orders: SimAvailableOrder[]
  ordersLoading: boolean
  orderSearch: string
}

const initialState: SimulationState = {
  mode: 'live',
  date: null,
  days: [],
  daysLoading: false,
  playing: false,
  seed: null,
  seedLoading: false,
  seedError: null,
  commands: [],
  currentMin: 0,
  runs: [],
  running: false,
  runError: null,
  compare: ['today', 'pooled'],
  orders: [],
  ordersLoading: false,
  orderSearch: '',
}

const simulationSlice = createSlice({
  name: 'queueSimulation',
  initialState,
  reducers: {
    // ── Seed ────────────────────────────────────────────────────────────
    fetchSeedRequest: (
      state,
      action: PayloadAction<{ factoryId?: string | number; mode?: SimStartMode; date?: string }>,
    ) => {
      state.seedLoading = true
      state.seedError = null
      if (action.payload?.mode) state.mode = action.payload.mode
      if (action.payload?.date !== undefined) state.date = action.payload.date ?? null
      // A run in progress belongs to the yard being replaced.
      state.playing = false
    },
    fetchSeedSuccess: (state, action: PayloadAction<SimSeed>) => {
      state.seedLoading = false
      state.seed = action.payload
      // A fresh reading of the yard invalidates everything done to the old one:
      // those commands were aimed at bays and trucks that have since moved.
      state.commands = []
      state.currentMin = 0
      state.runs = []
    },
    fetchSeedFailure: (state, action: PayloadAction<string>) => {
      state.seedLoading = false
      state.seedError = action.payload
    },

    // ── Commands ────────────────────────────────────────────────────────
    addCommand: (state, action: PayloadAction<SimCommand>) => {
      state.commands.push(action.payload)
    },
    undoLastCommand: (state) => {
      state.commands.pop()
    },
    clearCommands: (state) => {
      state.commands = []
      state.currentMin = 0
    },
    setCurrentMin: (state, action: PayloadAction<number>) => {
      state.currentMin = Math.max(0, action.payload)
    },
    advanceMinutes: (state, action: PayloadAction<number>) => {
      state.currentMin = Math.max(0, state.currentMin + action.payload)
    },
    setCompare: (state, action: PayloadAction<SimVariantKey[]>) => {
      state.compare = action.payload
    },
    setPlaying: (state, action: PayloadAction<boolean>) => {
      state.playing = action.payload
    },

    // ── Past days ───────────────────────────────────────────────────────
    fetchDaysRequest: (state, _action: PayloadAction<{ factoryId?: string | number }>) => {
      state.daysLoading = true
    },
    fetchDaysSuccess: (state, action: PayloadAction<SimReplayableDay[]>) => {
      state.daysLoading = false
      state.days = action.payload
    },
    fetchDaysFailure: (state) => {
      state.daysLoading = false
      state.days = []
    },

    // ── Run ─────────────────────────────────────────────────────────────
    runRequest: (state) => {
      state.running = true
      state.runError = null
    },
    runSuccess: (state, action: PayloadAction<{ runs: SimRun[]; atMin?: number }>) => {
      state.running = false
      state.runs = action.payload.runs
      if (typeof action.payload.atMin === 'number') state.currentMin = action.payload.atMin

      // Stop on its own once nothing is left to watch, so a demo does not run
      // silently past the end of the day with an audience watching.
      const finished = action.payload.runs.every(
        (r) => r.metrics.unserved === 0 && r.snapshot.pools.length === 0,
      )
      if (finished && action.payload.runs.length > 0) state.playing = false
    },
    runFailure: (state, action: PayloadAction<string>) => {
      state.running = false
      state.runError = action.payload
      state.playing = false
    },

    // ── Orders ──────────────────────────────────────────────────────────
    fetchOrdersRequest: (
      state,
      _action: PayloadAction<{ factoryId?: string | number; search?: string }>,
    ) => {
      state.ordersLoading = true
    },
    fetchOrdersSuccess: (state, action: PayloadAction<SimAvailableOrder[]>) => {
      state.ordersLoading = false
      state.orders = action.payload
    },
    fetchOrdersFailure: (state) => {
      state.ordersLoading = false
      state.orders = []
    },
    setOrderSearch: (state, action: PayloadAction<string>) => {
      state.orderSearch = action.payload
    },

    resetSimulation: () => initialState,
  },
})

export const simulationActions = simulationSlice.actions
export const simulationReducer = simulationSlice.reducer
export default simulationSlice.reducer
